<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { GripVertical, X } from 'lucide-vue-next'
import { type PageBreak } from '@/stores/clipboard'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
    defineProps<{
        page: PageBreak
        displayTitle: string
        removable?: boolean
    }>(),
    {
        removable: true
    }
)
const emit = defineEmits<{
    (e: 'remove', id: number): void
    (e: 'update-title', id: number, title: string): void
}>()

const { t } = useI18n()

const editing = ref(false)
const draft = ref('')
const inputEl = ref<HTMLInputElement | null>(null)

async function startEdit() {
    draft.value = props.page.title
    editing.value = true
    await nextTick()
    inputEl.value?.focus()
    inputEl.value?.select()
}

function saveEdit() {
    // Guard against double-emit: pressing Enter triggers saveEdit() AND, because
    // the input is then unmounted via v-if, the @blur listener fires saveEdit()
    // a second time. The guard makes the second call a no-op.
    if (!editing.value) return
    editing.value = false
    emit('update-title', props.page.id, draft.value.trim())
}

function cancelEdit() {
    editing.value = false
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
        e.preventDefault()
        saveEdit()
    } else if (e.key === 'Escape') {
        e.preventDefault()
        cancelEdit()
    }
}
</script>

<template>
    <div
        class="flex items-center gap-2 py-3 border-t-2 border-gray-300"
        :data-id="page.id"
    >
        <GripVertical class="clipboard-page-break-handle w-4 h-4 text-gray-400 shrink-0 cursor-grab active:cursor-grabbing" />
        <input
            v-if="editing"
            ref="inputEl"
            v-model="draft"
            type="text"
            class="clipboard-page-title-input flex-1 text-base font-semibold text-ink bg-white border border-teal rounded px-2 py-1 outline-none"
            :placeholder="displayTitle"
            @keydown="onKeydown"
            @blur="saveEdit"
        />
        <span
            v-else
            class="text-base font-semibold text-ink flex-1 truncate cursor-text hover:bg-gray-50 rounded px-2 py-1"
            :class="{ 'text-gray-400 italic': !page.title }"
            @click="startEdit"
        >
            {{ displayTitle }}
        </span>
        <button
            v-if="removable"
            type="button"
            class="text-gray-400 hover:text-red transition-colors shrink-0 p-1"
            :title="t('clipboard.pageBreak.remove')"
            @click="$emit('remove', page.id)"
        >
            <X class="w-4 h-4" />
        </button>
    </div>
</template>
